'use client';

import { Box, Grid2 as Grid, Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  DateSelect,
  SelectDataItem,
  SelectOption,
  TextFormControl,
} from '@abg/kit-base';
import { getCurrentRegionDateFormat } from '@abg/kit-base/lib/molecules/date-select/date-select.utils';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { useCommonDriverLicenseValidation } from '../../hooks';
import { useCountryInformation } from '../../hooks';
import { useGetDriverOptionYears } from '../../hooks/use-common-driver-license-validation/use-common-driver-license-validation';
import { useAccountInfo } from '../../organisms/AccountActivation/hooks/use-cbp-account-info';
import { validateMinimumAge } from '../../organisms/AccountActivation/organisms/DrivingLicense/age-validation.utils';
import {
  DRIVER_LICENSE_MIN_AGE,
  getMinDrivingAgeByIssuer,
  STATE_SELECTION_ISSUERS,
} from '../../shared/constants';
import { useTranslationContext } from '../../shared/context';
import { decodeHtmlEntities } from '../../shared/utils';
import { useCbpStore } from '../../store';

import {
  DriverLicenseFormLayout,
  DriverLicenseProperties,
} from './driver-license.types';

export function DriverLicenseForm({
  layout = DriverLicenseFormLayout.STATE_IN_LAST,
  defaultValues,
}: DriverLicenseProperties) {
  const { countries } = useCountryInformation();

  const { t, i18nKeys } = useTranslationContext();
  const { region } = usePageContext();
  const { driverLicenseNumberValidation } = useCommonDriverLicenseValidation();
  const currentRegionDateFormat = getCurrentRegionDateFormat(region);
  const formMethods = useFormContext();
  const { isCbpFlow } = useCbpStore();
  const { fetchAccountInfo } = useAccountInfo();
  const [cbpMinimumAge, setCbpMinimumAge] = useState<number | null>(null);

  const { trigger, setValue, watch, getValues, unregister } = formMethods;

  const [driverLicenseIssuer] = watch(['driverLicenseIssuer']) as [string];
  const issuerMinAge = getMinDrivingAgeByIssuer(driverLicenseIssuer);

  // Fetch CBP enrollment details to get minimum age when in CBP flow
  useEffect(() => {
    if (!isCbpFlow) return;

    void (async () => {
      const accountInfo = await fetchAccountInfo();
      if (accountInfo?.minimumAge) {
        setCbpMinimumAge(accountInfo.minimumAge);
      }
    })();
  }, [isCbpFlow, fetchAccountInfo]);

  // Use CBP minimum age if valid, otherwise fall back to issuer-based age
  const minimumAge = cbpMinimumAge ?? issuerMinAge;

  const hasStateSelection =
    STATE_SELECTION_ISSUERS.includes(driverLicenseIssuer);

  useEffect(() => {
    if (!hasStateSelection && getValues('driverLicenseState')) {
      setValue('driverLicenseState', '');
      unregister('driverLicenseState');
      void trigger(['driverLicenseIssuer', 'driverLicenseNumber', 'dob']);
    }
  }, [setValue, trigger, unregister, getValues, hasStateSelection]);

  const countryOptions = useMemo(
    () =>
      countries.map((country) => ({
        label: country.countryName,
        value: country.isoCountryCode,
        states: country.states
          ?.filter((state) => state.stateName?.trim())
          .sort((a, b) =>
            (a.stateName as string).localeCompare(b.stateName as string),
          )
          .map((state) => ({
            label: state.stateName,
            value: state.stateCode,
          })),
      })),
    [countries],
  );

  const stateOptions = useMemo(() => {
    const selectedCountryData = countries.find(
      (country) => country.isoCountryCode === driverLicenseIssuer,
    );

    return selectedCountryData?.isoCountryCode &&
      STATE_SELECTION_ISSUERS.includes(selectedCountryData.isoCountryCode)
      ? selectedCountryData?.states
          ?.filter(
            (state) =>
              state?.stateName?.trim() !== '' &&
              state?.stateCode?.trim() !== '',
          )
          .sort((a, b) =>
            (a.stateName as string).localeCompare(b.stateName as string),
          )
          .map((state) => ({
            label: state.stateName,
            value: state.stateCode,
          })) || []
      : [];
  }, [countries, driverLicenseIssuer]);

  const validateAge = (value: string) => {
    if (!value) return true; // Let required validation handle empty values

    const requiredMinAge = minimumAge || DRIVER_LICENSE_MIN_AGE; // Default to 21 if not provided

    if (!validateMinimumAge(value, requiredMinAge)) {
      return t(i18nKeys['ageMinimumRequirementMessage'] as string, {
        Age: requiredMinAge.toString(),
      });
    }

    return true;
  };

  const optionsYear = useGetDriverOptionYears();

  return (
    <Box data-testid="driver-license-form-container">
      {layout === DriverLicenseFormLayout.STATE_IN_LAST ? (
        <Stack direction="column" rowGap={3}>
          <Grid container rowSpacing={2} columnSpacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextFormControl
                name="driverLicenseIssuer"
                testId="security-code"
                required
                select
                selectData={countryOptions as SelectOption[]}
                label={t(i18nKeys['driverLicenseIssuerLabel'] as string)}
                placeholder={t(
                  i18nKeys['driverLicenseIssuerPlaceholder'] as string,
                )}
                textFieldProps={{
                  fullWidth: true,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextFormControl
                name="driverLicenseNumber"
                required
                label={t(i18nKeys['driverLicenseNumberLabel'] as string)}
                placeholder={t(
                  i18nKeys['driverLicenseNumberPlaceholder'] as string,
                )}
                validationRules={driverLicenseNumberValidation}
                textFieldProps={{
                  fullWidth: true,
                  autoComplete: 'off',
                }}
                testId="license-number"
              />
            </Grid>
          </Grid>

          <Grid container rowSpacing={3} columnSpacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DateSelect
                required
                label={t(i18nKeys['driverLicenseDobLabel'] as string)}
                {...formMethods.register('dob', {
                  required: t(i18nKeys['requiredMessageInfo'] as string, {
                    field: t(i18nKeys['driverLicenseDobLabel'] as string),
                  }),
                  validate: validateAge,
                })}
                errors={
                  formMethods.formState.errors as Record<
                    string,
                    { message: string }
                  >
                }
                defaultValue={defaultValues?.dob || ''}
                dateFormat={currentRegionDateFormat}
                optionsYear={optionsYear}
                onChange={(value: string) => {
                  setValue('dob', value);
                  void trigger('dob');
                }}
                data-testid="license-dob"
              />
            </Grid>

            {hasStateSelection && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextFormControl
                  name="driverLicenseState"
                  testId="license-state"
                  required
                  select
                  selectData={stateOptions as SelectDataItem[]}
                  label={t(
                    i18nKeys['driverLicenseStateProvinceLabel'] as string,
                  )}
                  placeholder={t(
                    i18nKeys['driverLicenseStateProvincePlaceholder'] as string,
                  )}
                  textFieldProps={{
                    fullWidth: true,
                  }}
                  validationRules={{
                    required: decodeHtmlEntities(
                      t(i18nKeys['requiredMessageInfo'] as string, {
                        field: t(
                          i18nKeys['driverLicenseStateProvinceLabel'] as string,
                        ),
                      }),
                    ),
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Stack>
      ) : (
        <Stack direction="column" rowGap={3}>
          <Grid container rowSpacing={3} columnSpacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextFormControl
                name="driverLicenseIssuer"
                testId="driver-license-issuer-select"
                required
                select
                selectData={countryOptions as SelectOption[]}
                label={t(i18nKeys['driverLicenseIssuerLabel'] as string)}
                placeholder={t(
                  i18nKeys['driverLicenseIssuerPlaceholder'] as string,
                )}
                textFieldProps={{
                  fullWidth: true,
                }}
                validationRules={{
                  required: t(i18nKeys['requiredMessageInfo'] as string, {
                    field: t(i18nKeys['driverLicenseIssuerLabel'] as string),
                  }),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              {
                /*
                 * If the issuer is a country that requires state selection,
                 * show the state select field next to the country select field
                 * and move the license number field to the next line.
                 */
                hasStateSelection ? (
                  <TextFormControl
                    name="driverLicenseState"
                    testId="license-state"
                    required
                    select
                    selectData={stateOptions as SelectDataItem[]}
                    label={t(
                      i18nKeys['driverLicenseStateProvinceLabel'] as string,
                    )}
                    placeholder={t(
                      i18nKeys[
                        'driverLicenseStateProvincePlaceholder'
                      ] as string,
                    )}
                    textFieldProps={{
                      fullWidth: true,
                    }}
                    validationRules={{
                      required: decodeHtmlEntities(
                        t(i18nKeys['requiredMessageInfo'] as string, {
                          field: t(
                            i18nKeys[
                              'driverLicenseStateProvinceLabel'
                            ] as string,
                          ),
                        }),
                      ),
                    }}
                  />
                ) : (
                  <TextFormControl
                    name="driverLicenseNumber"
                    required
                    label={t(i18nKeys['driverLicenseNumberLabel'] as string)}
                    placeholder={t(
                      i18nKeys['driverLicenseNumberPlaceholder'] as string,
                    )}
                    validationRules={driverLicenseNumberValidation}
                    textFieldProps={{
                      fullWidth: true,
                      autoComplete: 'off',
                    }}
                    testId="license-number"
                  />
                )
              }
            </Grid>
          </Grid>

          {
            /*
             * Render the license number field only if the state selection is visible on previous line.
             * Otherwise, license number field will be rendered in previous line.
             */
            hasStateSelection && (
              <Grid container rowSpacing={3} columnSpacing={2}>
                <Grid size={12}>
                  <TextFormControl
                    name="driverLicenseNumber"
                    required
                    label={t(i18nKeys['driverLicenseNumberLabel'] as string)}
                    placeholder={t(
                      i18nKeys['driverLicenseNumberPlaceholder'] as string,
                    )}
                    validationRules={driverLicenseNumberValidation}
                    textFieldProps={{
                      fullWidth: true,
                      autoComplete: 'off',
                    }}
                    testId="license-number"
                  />
                </Grid>
              </Grid>
            )
          }

          <Grid container rowSpacing={3} columnSpacing={2}>
            <Grid size={12}>
              <DateSelect
                required
                label={t(i18nKeys['driverLicenseDobLabel'] as string)}
                {...formMethods.register('dob', {
                  required: t(i18nKeys['requiredMessageInfo'] as string, {
                    field: t(i18nKeys['driverLicenseDobLabel'] as string),
                  }),
                  validate: validateAge,
                })}
                errors={
                  formMethods.formState.errors as Record<
                    string,
                    { message: string }
                  >
                }
                defaultValue={defaultValues?.dob || ''}
                dateFormat={currentRegionDateFormat}
                optionsYear={optionsYear}
                onChange={(value: string) => {
                  setValue('dob', value);
                  void trigger('dob');
                }}
                data-testid="license-dob"
              />
            </Grid>
          </Grid>
        </Stack>
      )}
    </Box>
  );
}
